import axios from 'axios';
import { Formik, FormikHelpers } from 'formik';
import React from 'react';

import { Box, Typography } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { Add } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { User } from '@uplift/api';
import { UserRole } from '@uplift/types';

import { Alert } from '../../../Alert';
import { Button } from '../../../Button';
import { CancelButton, FormModal, SubmitButton } from '../../../Form';
import { AsyncAutocomplete, AutocompleteHighlighter } from '../../../Inputs';
import { ModalActions, ModalContent } from '../../../Modal';
import {
  useCreateMatch, useGetProvidersUpliftLicensesTypes, useUsersSearch
} from './CareTeamList.hooks';
import { getProviderLabelByMedicalRoleAndPrescriberTypes } from './helpers';

const PurpleAutocompleteHighlighter = withStyles(AutocompleteHighlighter, theme => ({
  highlight: {
    color: theme.palette.secondary.main
  }
}));

type FormType = {
  open: boolean;
  patientId: string;
  provider: User | null;
};

export const CreateMatchButton: React.FC<{ patientId: string }> = ({ patientId }) => {
  const queryClient = useQueryClient();
  const { usersSearch } = useUsersSearch();
  const { getProvidersUpliftLicensesTypes } = useGetProvidersUpliftLicensesTypes();
  const { createMatch } = useCreateMatch();
  const [ open, setOpen ] = React.useState(false);

  const handleCreateNewMatch = async (values: FormType, helpers: FormikHelpers<FormType>) => {
    if (values.provider) {
      try {
        await createMatch({
          patientId: values.patientId,
          providerId: values.provider.id,
        });

        await queryClient.invalidateQueries({ queryKey: [ 'user', values.patientId, 'matches' ] });
        setOpen(false);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          if (err.response?.data?.error?.includes('Active match')) {
            helpers.setFieldError('provider', 'An active match with this provider already exists.');
          }
        } else {
          helpers.setFieldError('provider', 'An unknown error occurred. Please contact support.');
        }
      }
    }
  };

  const initialValues: FormType = {
    open: open,
    patientId: patientId,
    provider: null,
  };

  return (
    <>
      <Button
        color="secondary"
        startIcon={<Add />}
        onClick={() => setOpen(true)}
      >
        Add Match
      </Button>
      <Formik
        enableReinitialize
        validateOnMount
        onSubmit={handleCreateNewMatch}
        initialValues={initialValues}
      >
        {formik => (
          <FormModal
            open={open}
            onClose={() => setOpen(false)}
            title="Create New Match"
            fullWidth
            maxWidth="md"
          >
            <ModalContent>
              {formik.errors.provider && (
                <Box mb={3}>
                  <Alert severity="error">
                    {formik.errors.provider}
                  </Alert>
                </Box>
              )}
              <AsyncAutocomplete
                fullWidth
                autoFocus
                name="provider"
                label="Provider"
                helperText="Start typing provider name or email"
                getOptionLabel={(u: (User & { prescriberLabel?: string })) => `${u.firstName} ${u.lastName}, ${u.email}, ${u.prescriberLabel}`}
                renderOption={(opt, state) => (
                  <Box>
                    <Box display="flex" whiteSpace="nowrap" gap="4px">
                      <Typography component="span">
                        <PurpleAutocompleteHighlighter text={`${opt.firstName} ${opt.lastName}`} highlight={state.inputValue} />, <PurpleAutocompleteHighlighter text={opt.email} highlight={state.inputValue} />
                      </Typography>
                    </Box>
                    <Typography color="textSecondary">{opt.prescriberLabel}</Typography>
                  </Box>
                )}
                asyncOptions={async (term) => {
                  const { data: { users } } = await usersSearch(term, UserRole.provider);
                  const providersUpliftLicensesTypes = await getProvidersUpliftLicensesTypes(users.map(u => u.id));

                  return users.map(u => {
                    const providerLicenseTypes = u.id ? providersUpliftLicensesTypes.data[u.id] : [];
                    const prescriberLabel = getProviderLabelByMedicalRoleAndPrescriberTypes(
                      u.medicalRole!,
                      providerLicenseTypes,
                    );

                    return {
                      ...u,
                      prescriberLabel: prescriberLabel,
                    };
                  });
                }}
              />
              <Box height="200px" />
            </ModalContent>
            <ModalActions>
              <CancelButton onClick={() => setOpen(false)}>Cancel</CancelButton>
              <SubmitButton color="secondary">Create Match</SubmitButton>
            </ModalActions>
          </FormModal>
        )}
      </Formik>
    </>
  );
};
